// ============================================================================
// GalleryPost.jsx — Dedicated single gallery post page for shareable links
// Route: /gallery/:postId
// ============================================================================

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import ShareButtons from '../components/ShareButtons'

const API_URL = import.meta.env.VITE_API_URL || ''

export default function GalleryPost() {
  const { postId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const userEmail = user?.email || ''

  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!postId) return
    fetchPost()
    fetchComments()
  }, [postId])

  // Update OG meta tags dynamically for social crawlers (client-side fallback)
  useEffect(() => {
    if (!post) return
    const img = post.image_urls?.[0] || post.thumbnail_url
    document.title = `${post.title || 'Design'} — Zenna Gallery`

    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('property', property)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }

    setMeta('og:title', post.title || 'Zenna Gallery')
    setMeta('og:description', post.description || `${post.craft_type || 'Craft'} design created with Zenna AI`)
    setMeta('og:image', img)
    setMeta('og:url', window.location.href)
    setMeta('og:type', 'article')
    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:title', post.title || 'Zenna Gallery')
    setMeta('twitter:image', img)

    return () => {
      document.title = 'Zenna - Your complete Craft Companion'
    }
  }, [post])

  const fetchPost = async () => {
    try {
      const headers = {}
      if (userEmail) headers['x-user-email'] = userEmail
      const res = await fetch(`${API_URL}/community/gallery/${postId}`, { headers })
      if (!res.ok) throw new Error('Post not found')
      const data = await res.json()
      setPost(data.post)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API_URL}/community/gallery/${postId}/comments`)
      const data = await res.json()
      setComments(data.comments || [])
    } catch (e) { console.error(e) }
  }

  const handleLike = async () => {
    if (!userEmail) return
    try {
      const res = await fetch(`${API_URL}/community/gallery/${postId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail }
      })
      const data = await res.json()
      if (data.success) {
        setPost(prev => ({
          ...prev,
          is_liked: data.action === 'liked',
          like_count: data.action === 'liked' ? (prev.like_count || 0) + 1 : Math.max(0, (prev.like_count || 0) - 1)
        }))
      }
    } catch (e) { console.error(e) }
  }

  const handleComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim() || submitting || !userEmail) return
    setSubmitting(true)
    try {
      const res = await fetch(`${API_URL}/community/gallery/${postId}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify({ content: newComment.trim() })
      })
      const data = await res.json()
      if (data.success) {
        setComments(prev => [...prev, data.comment])
        setNewComment('')
      }
    } catch (e) { console.error(e) }
    setSubmitting(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white font-bold text-xl">Z</span>
          </div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Post Not Found</h1>
          <p className="text-slate-400 mb-6">This design may have been removed or the link is invalid.</p>
          <Link to="/gallery" className="px-6 py-2 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white rounded-lg hover:opacity-90 transition">
            Browse Gallery
          </Link>
        </div>
      </div>
    )
  }

  const mainImage = post.image_urls?.[0] || post.thumbnail_url
  const timeAgo = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000)
    if (s < 60) return 'just now'
    if (s < 3600) return `${Math.floor(s / 60)}m ago`
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`
    if (s < 2592000) return `${Math.floor(s / 86400)}d ago`
    return new Date(date).toLocaleDateString()
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Nav bar */}
      <nav className="border-b border-slate-800 px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center">
            <span className="text-white font-bold text-sm">Z</span>
          </div>
          <span className="text-white font-semibold">Zenna</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/gallery" className="text-sm text-slate-400 hover:text-white transition">Gallery</Link>
          {userEmail ? (
            <Link to="/dashboard" className="text-sm text-slate-400 hover:text-white transition">Dashboard</Link>
          ) : (
            <Link to="/" className="px-4 py-1.5 text-sm bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white rounded-lg hover:opacity-90 transition">
              Sign Up Free
            </Link>
          )}
        </div>
      </nav>

      {/* Main content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Image */}
          <div className="flex-1">
            <div className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700">
              <img
                src={mainImage}
                alt={post.title}
                className="w-full object-contain max-h-[70vh]"
              />
            </div>

            {/* Multiple images */}
            {post.image_urls?.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {post.image_urls.map((url, i) => (
                  <img key={i} src={url} alt="" className="w-20 h-20 object-cover rounded-lg border border-slate-700 cursor-pointer hover:border-zenna-cyan transition" />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-80 space-y-6">
            {/* Author */}
            <div className="flex items-center gap-3">
              {post.author?.avatar_url ? (
                <img src={post.author.avatar_url} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center text-white font-bold">
                  {(post.author?.display_name || '?')[0]}
                </div>
              )}
              <div>
                <div className="text-white font-medium">{post.author?.display_name || 'Anonymous'}</div>
                <div className="text-xs text-slate-500">{timeAgo(post.created_at)}</div>
              </div>
            </div>

            {/* Title & description */}
            <div>
              <h1 className="text-xl font-bold text-white mb-2">{post.title}</h1>
              {post.description && <p className="text-sm text-slate-400">{post.description}</p>}
            </div>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {post.tags.map(tag => (
                  <span key={tag} className="text-xs text-zenna-cyan/80 bg-zenna-cyan/10 px-2.5 py-1 rounded-full">#{tag}</span>
                ))}
              </div>
            )}

            {/* Craft type badge */}
            {post.craft_type && (
              <div className="inline-block text-xs font-medium text-zenna-violet bg-zenna-violet/10 px-3 py-1 rounded-full">
                {post.craft_type}
              </div>
            )}

            {/* Remix button */}
            <button
              onClick={() => {
                const img = post.image_urls?.[0] || post.thumbnail_url
                const craftType = post.craft_type || ''
                const remixTitle = `Remix of: ${post.title || 'Gallery design'}`
                // Navigate to workspace with the image preloaded via query params
                navigate(`/workspace?remix=true&remixImage=${encodeURIComponent(img)}&remixCraft=${encodeURIComponent(craftType)}&remixTitle=${encodeURIComponent(remixTitle)}`)
              }}
              className="w-full py-2.5 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-medium rounded-xl hover:opacity-90 transition flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              Remix This Design
            </button>

            {/* Stats & actions */}
            <div className="flex items-center gap-4 py-3 border-t border-b border-slate-700">
              <button
                onClick={handleLike}
                className={`flex items-center gap-1.5 text-sm transition ${post.is_liked ? 'text-red-400' : 'text-slate-400 hover:text-red-400'}`}
              >
                {post.is_liked ? '❤️' : '🤍'} {post.like_count || 0}
              </button>
              <span className="text-sm text-slate-400">💬 {comments.length || post.comment_count || 0}</span>
              <span className="text-sm text-slate-400">👁 {post.view_count || 0}</span>
            </div>

            {/* Share */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Share</div>
              <div className="flex gap-2">
                <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="px-3 py-1.5 text-xs bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition">
                  Copy Link
                </button>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener" className="px-3 py-1.5 text-xs bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition">
                  Facebook
                </a>
                <a href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.href)}&media=${encodeURIComponent(mainImage)}&description=${encodeURIComponent(post.title || 'Zenna design')}`} target="_blank" rel="noopener" className="px-3 py-1.5 text-xs bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition">
                  Pinterest
                </a>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`Check out this ${post.craft_type || 'craft'} design on Zenna!`)}`} target="_blank" rel="noopener" className="px-3 py-1.5 text-xs bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition">
                  X
                </a>
              </div>
            </div>

            {/* CTA for non-logged-in users */}
            {!userEmail && (
              <div className="bg-gradient-to-r from-zenna-violet/10 to-zenna-cyan/10 border border-zenna-violet/20 rounded-xl p-4 text-center">
                <p className="text-sm text-white mb-2">Create your own designs with Zenna</p>
                <Link to="/" className="inline-block px-6 py-2 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-medium rounded-lg hover:opacity-90 transition">
                  Get Started Free
                </Link>
              </div>
            )}

            {/* Comments */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Comments ({comments.length})
              </div>

              {userEmail && (
                <form onSubmit={handleComment} className="flex gap-2 mb-4">
                  <input
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet"
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim() || submitting}
                    className="px-3 py-2 bg-zenna-violet text-white text-sm rounded-lg hover:bg-zenna-violet/80 transition disabled:opacity-40"
                  >
                    Post
                  </button>
                </form>
              )}

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {comments.length === 0 && (
                  <p className="text-sm text-slate-600">No comments yet. Be the first!</p>
                )}
                {comments.map(c => (
                  <div key={c.id} className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-slate-400 font-bold shrink-0">
                      {(c.author?.display_name || '?')[0]}
                    </div>
                    <div>
                      <span className="text-xs font-medium text-slate-300">{c.author?.display_name || 'Anon'}</span>
                      <span className="text-xs text-slate-600 ml-2">{timeAgo(c.created_at)}</span>
                      <p className="text-sm text-slate-400">{c.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
